import { ApplicationSelection } from '~types/app-selection';
import type { KpiEnvelope } from '~stores/kpi-envelope';
import { envelopeWarnings } from '~stores/kpi-envelope';
import type { KpiTrend } from '~containers/analytics/kpis/types';

/**
 * Helpers for the throwaway Taxi Driver campaign dashboard.
 *
 * This page is deliberately self-contained: it consumes the campaign backend
 * contract directly and REUSES only the shared VISUAL layer (KpiCardShell +
 * KpiTile). It never registers anything in the standardized KPI source registry
 * (`SOURCES`, `KpiSourceId`, `KpiMetric`), so the whole feature is deletable as
 * one container folder + one nav line + one route line + one nav method.
 *
 * The campaign ends 2026-09-14, so nothing here is meant to outlive it.
 */

/** Campaign id owned entirely by this throwaway page. */
export const CAMPAIGN_ID = 'taxi-2026';

/** One of the four KPI tiles, each reading a single scalar metric from the
 * campaign-purchases envelope. */
export interface TaxiTileSpec {
    key: string;
    title: string;
    metricId: string;
    isCurrency: boolean;
}

/**
 * The four period-scoped tiles. `revenue` -> Total spend, `avgSpend` ->
 * Avg. spend / transaction, `transactions` -> Total visits, `individuals` ->
 * Activated phone numbers (distinct phones with at least one purchase in the
 * period). Order matches the design's left-to-right layout.
 */
export const TAXI_TILE_SPECS: TaxiTileSpec[] = [
    {
        key: 'revenue',
        title: 'Total spend',
        metricId: 'revenue',
        isCurrency: true,
    },
    {
        key: 'avgSpend',
        title: 'Avg. spend / transaction',
        metricId: 'avgSpend',
        isCurrency: true,
    },
    {
        key: 'transactions',
        title: 'Total visits',
        metricId: 'transactions',
        isCurrency: false,
    },
    {
        key: 'individuals',
        title: 'Activated phone numbers',
        metricId: 'individuals',
        isCurrency: false,
    },
];

/** Tile display data derived from the envelope: the value, its currency flag, and
 * an optional trend (present only when Compare is on and the metric carries a
 * compare block). */
export interface TaxiTileData {
    value: number;
    isCurrency: boolean;
    trend?: KpiTrend;
}

/**
 * The campaign-purchases envelope carries no `degradation` block for an empty
 * window; the backend flags emptiness through a `warning` instead (see its
 * `emptyPeriodCaveat`). In an empty window every scalar is 0 "by construction",
 * not a measured $0, so the tiles must read that signal and show no data.
 *
 * Read by CODE, not by matching a fragment of the prose. This used to search
 * the caveat text for '(empty period)' and 'comparison period', which a reword
 * or a translation would have broken silently; the envelope now types the two
 * apart and names each warning.
 */
const EMPTY_PERIOD_CODE = 'empty_period';
const EMPTY_COMPARISON_CODE = 'empty_comparison_period';

const hasWarning = (env: KpiEnvelope, code: string): boolean =>
    envelopeWarnings(env.caveats).some((entry) => entry.code === code);

/** True when the main window is empty (its scalars are 0 by construction). */
const hasEmptyPeriodCaveat = (env: KpiEnvelope): boolean =>
    hasWarning(env, EMPTY_PERIOD_CODE);

/** True when the compare baseline is empty, so a delta against it isn't real. */
const hasEmptyComparisonCaveat = (env: KpiEnvelope): boolean =>
    hasWarning(env, EMPTY_COMPARISON_CODE);

/**
 * Map one scalar envelope metric into tile display data. Mirrors the shared
 * envelope source's number-form logic (value + absolute compare delta) WITHOUT
 * going through the KPI source registry, so this throwaway page adds nothing to
 * the standardized data layer.
 *
 * The delta is the absolute change in the metric's own unit (dollars for a
 * currency metric, a plain count otherwise), matching how the existing number
 * cards render "+X from last period".
 *
 * Returns `null` when the envelope is unavailable, the window is empty, or the
 * metric is absent / has no value, so the tile shows an unavailable state
 * instead of a fabricated 0.
 *
 * `avgSpend` is a ratio (an average carried as revenue-over-priced-baskets),
 * the other three tiles are scalars, so both are read here. A ratio's `rate` is
 * null when nothing was priced, which is the same "no data" this function used
 * to infer from the empty-period caveat; now the metric says it directly, and
 * the caveat check stays only for the case it alone can catch: a window with
 * transactions but no revenue, where every scalar is a real, measured 0.
 */
export const buildTaxiTileData = (
    env: KpiEnvelope | null,
    spec: TaxiTileSpec,
    comparisonLabel: string | undefined
): TaxiTileData | null => {
    if (!env) return null;
    // An unavailable envelope or an empty window (all-zero by construction) is
    // not a measured 0: show no data rather than a fabricated $0.
    if (env.degradation || hasEmptyPeriodCaveat(env)) {
        return null;
    }
    const metric = env.metrics[spec.metricId];
    if (!metric || (metric.type !== 'scalar' && metric.type !== 'ratio')) {
        return null;
    }

    const value = metric.type === 'ratio' ? metric.rate : metric.value;
    if (value == null) return null;

    let trend: KpiTrend | undefined;
    // Attach a trend only when Compare is on (a label is supplied), the backend
    // returned a compare block, AND the compare window itself is not empty (a
    // delta against an empty baseline would read as a full-value change against
    // a measured $0).
    if (
        comparisonLabel !== undefined &&
        metric.compare &&
        !hasEmptyComparisonCaveat(env)
    ) {
        const compareValue =
            metric.type === 'ratio'
                ? metric.compare.rate
                : metric.compare.value;
        // A null compare rate is an unmeasured baseline, not a zero one: no
        // trend rather than a delta equal to the whole current value.
        if (compareValue != null) {
            const delta = value - compareValue;
            trend = {
                delta,
                unit: spec.isCurrency ? 'currency' : 'count',
                direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
                comparisonLabel,
            };
        }
    }
    return { value, isCurrency: spec.isCurrency, trend };
};

/** Currency format matching the shared KpiTile / RPP business-impact cards: a
 * `$` prefix over `toLocaleString('en-US')`. Defaults to no forced decimals (as
 * KpiTile does); pass `fractionDigits` to pin the decimals for an inherently
 * fractional column (the Avg. spend / transaction column uses 2). */
export const formatTaxiCurrency = (
    value: number,
    fractionDigits?: number
): string =>
    `$${value.toLocaleString(
        'en-US',
        fractionDigits === undefined
            ? undefined
            : {
                  minimumFractionDigits: fractionDigits,
                  maximumFractionDigits: fractionDigits,
              }
    )}`;

/**
 * Nav gating: the Taxi driver analytics item is Couche-Tard-only (hidden for
 * Circle K and All Regions). A pure predicate so the gating is unit-testable and
 * lives inside the deletable container.
 */
export const isTaxiDriverNavVisible = (
    selectedApplication: ApplicationSelection
): boolean => selectedApplication === ApplicationSelection.COUCHE_TARD;
